import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Input,
  Skeleton,
  Spinner,
  Textarea,
  toast
} from "@veritrellis/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronRight, Inbox } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { CopyButton } from "../components/CopyButton.js";
import { apiFetch } from "../lib/api.js";
import {
  formatExpiry,
  policyModeBadgeVariant,
  policyModeLabel
} from "../lib/approvalPresentation.js";
import { envLabel, formatDateTimeShort } from "../lib/requestPresentation.js";
import { usePersistedAdminEnvironment } from "../hooks/usePersistedAdminEnvironment.js";
import { roleAtLeast, useCurrentRole } from "../hooks/useCurrentRole.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";

type Environment = "sandbox" | "production";

type ApprovalRow = {
  id: string;
  environment: Environment;
  action_type: string;
  policy_mode: string | null;
  created_at: string;
  expires_at: string;
  resource_ref: string | null;
};

type ApprovalsResponse = {
  inbox_environment: Environment;
  requests: ApprovalRow[];
};

const InboxItem = (input: {
  workspaceId: string;
  environment: Environment;
  row: ApprovalRow;
  canAct: boolean;
}) => {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState("");
  const expiry = formatExpiry(input.row.expires_at);

  const decide = useMutation({
    mutationFn: async (decision: "approve" | "reject") => {
      const res = await apiFetch(
        `/admin/workspaces/${input.workspaceId}/requests/${input.row.id}/${decision}`,
        {
          method: "POST",
          body: JSON.stringify({
            environment: input.environment,
            comment: comment.length > 0 ? comment : undefined
          })
        }
      );
      if (!res.ok) {
        throw new Error(`decision_${String(res.status)}`);
      }
      return decision;
    },
    onSuccess: (decision) => {
      toast.show({
        title: decision === "approve" ? "Approved" : "Rejected",
        message: `${input.row.action_type} decided.`,
        color: decision === "approve" ? "green" : "red"
      });
      void queryClient.invalidateQueries({ queryKey: ["approvals", input.workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ["requests", input.workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ["summary", input.workspaceId] });
      void queryClient.invalidateQueries({
        queryKey: ["request", input.workspaceId, input.row.id]
      });
    },
    onError: (err: Error) => {
      const msg = err.message === "decision_409" ? "No longer pending" : "Decision failed";
      toast.show({ title: "Error", message: msg, color: "red" });
      void queryClient.invalidateQueries({ queryKey: ["approvals", input.workspaceId] });
    }
  });

  const accentClass = expiry.expired
    ? "admin-inbox-item-accent-expired"
    : expiry.urgent
      ? "admin-inbox-item-accent-urgent"
      : "";
  const itemClass = expiry.expired
    ? "admin-inbox-item admin-inbox-item-expired"
    : expiry.urgent
      ? "admin-inbox-item admin-inbox-item-urgent"
      : "admin-inbox-item";
  const modeLabel = policyModeLabel(input.row.policy_mode);

  return (
    <article className={itemClass}>
      <div
        className={`admin-inbox-item-accent${accentClass.length > 0 ? ` ${accentClass}` : ""}`}
      />
      <div className="admin-inbox-item-body">
        <div className="admin-inbox-item-top">
          <div className="admin-inbox-item-main">
            <div className="admin-inbox-item-title-row">
              <h3 className="admin-inbox-item-title">{input.row.action_type}</h3>
              {modeLabel !== null ? (
                <Badge variant={policyModeBadgeVariant(input.row.policy_mode)}>{modeLabel}</Badge>
              ) : null}
              <Badge variant="outline">{envLabel(input.row.environment)}</Badge>
            </div>
            <div className="admin-inbox-item-id">
              <span>{input.row.id}</span>
              <CopyButton value={input.row.id} label="Copy request ID" />
            </div>
            {input.row.resource_ref !== null ? (
              <p className="admin-inbox-item-meta">
                Resource <code>{input.row.resource_ref}</code>
              </p>
            ) : null}
          </div>
          <div className="admin-inbox-item-aside">
            <span className="admin-inbox-item-time">
              Created {formatDateTimeShort(input.row.created_at)}
            </span>
            <Badge
              variant={expiry.expired ? "destructive" : expiry.urgent ? "warning" : "secondary"}
            >
              {expiry.label}
            </Badge>
          </div>
        </div>

        {input.canAct ? (
          <div className="admin-inbox-item-actions">
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.currentTarget.value)}
              placeholder="Comment (recommended when rejecting)"
              rows={2}
            />
            <div className="admin-inbox-item-action-row">
              <Button
                variant="outline"
                size="sm"
                disabled={decide.isPending}
                onClick={() => decide.mutate("reject")}
              >
                {decide.isPending && decide.variables === "reject" ? (
                  <Spinner style={{ width: 14, height: 14, marginRight: 4 }} />
                ) : null}
                Reject
              </Button>
              <Button
                size="sm"
                disabled={decide.isPending}
                onClick={() => decide.mutate("approve")}
              >
                {decide.isPending && decide.variables === "approve" ? (
                  <Spinner style={{ width: 14, height: 14, marginRight: 4 }} />
                ) : null}
                Approve
              </Button>
            </div>
          </div>
        ) : null}

        <div className="admin-inbox-item-footer">
          <Link to={`/requests/${input.row.id}`} className="admin-inbox-item-link">
            View full request
            <ChevronRight style={{ width: 14, height: 14 }} />
          </Link>
        </div>
      </div>
    </article>
  );
};

export const ApprovalsPage = () => {
  const { workspaceId, isLoading } = useWorkspaceId();
  const { role } = useCurrentRole();
  const [environment] = usePersistedAdminEnvironment();
  const [search, setSearch] = useState("");

  const approvals = useQuery({
    queryKey: ["approvals", workspaceId, environment],
    enabled: workspaceId !== null,
    queryFn: async () => {
      const res = await apiFetch(
        `/admin/workspaces/${workspaceId}/approvals?environment=${environment}`
      );
      if (!res.ok) {
        throw new Error(`approvals_${String(res.status)}`);
      }
      return (await res.json()) as ApprovalsResponse;
    }
  });

  const rows = approvals.data?.requests ?? [];

  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime()),
    [rows]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term.length === 0) {
      return sorted;
    }
    return sorted.filter(
      (r) =>
        r.id.toLowerCase().includes(term) ||
        r.action_type.toLowerCase().includes(term) ||
        (r.resource_ref?.toLowerCase().includes(term) ?? false)
    );
  }, [sorted, search]);

  const stats = useMemo(() => {
    const urgent = rows.filter((r) => formatExpiry(r.expires_at).urgent).length;
    const expired = rows.filter((r) => formatExpiry(r.expires_at).expired).length;
    return { total: rows.length, urgent, expired };
  }, [rows]);

  if (isLoading) {
    return (
      <div className="admin-page-stack">
        <Skeleton style={{ height: 48, width: "45%" }} />
        <Skeleton style={{ height: 200, width: "100%" }} />
      </div>
    );
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }

  const canAct = roleAtLeast(role, "approver");

  return (
    <div className="admin-page-stack">
      {!canAct ? (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTitle>Read-only</AlertTitle>
          <AlertDescription>
            Your role is <strong>{role ?? "unknown"}</strong>. You can see the queue but cannot
            approve or reject. Ask an owner to grant the <code>approver</code> role.
          </AlertDescription>
        </Alert>
      ) : null}

      {stats.urgent > 0 && !approvals.isLoading ? (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle
            style={{ width: 16, height: 16, flexShrink: 0, color: "#b45309" }}
            aria-hidden
          />
          <AlertTitle>Expiring soon</AlertTitle>
          <AlertDescription>
            {stats.urgent} {stats.urgent === 1 ? "request needs" : "requests need"} a decision
            within the next hour.
            {stats.expired > 0 ? ` ${String(stats.expired)} already past expiry.` : null}
          </AlertDescription>
        </Alert>
      ) : null}

      <section className="admin-panel" aria-labelledby="inbox-queue-heading">
        <div className="admin-panel-header">
          <h2 id="inbox-queue-heading" className="admin-panel-title">
            Queue
          </h2>
          {stats.total > 0 ? (
            <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
              {stats.total} waiting
            </span>
          ) : null}
        </div>

        {rows.length > 0 ? (
          <div className="admin-filter-toolbar">
            <div className="admin-search-field" style={{ maxWidth: "100%" }}>
              <label className="admin-search-label" htmlFor="inbox-search">
                Search queue
              </label>
              <Input
                id="inbox-search"
                type="search"
                placeholder="Action, resource, or request ID…"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
              />
            </div>
          </div>
        ) : null}

        {!approvals.isLoading && rows.length > 0 ? (
          <div className="admin-count-strip">
            <span>
              Showing <strong>{filtered.length}</strong> of <strong>{stats.total}</strong>
            </span>
            {stats.urgent > 0 ? (
              <span>
                <strong>{stats.urgent}</strong> expiring soon
              </span>
            ) : null}
            <span>Sorted by expiry</span>
          </div>
        ) : null}

        <div className="admin-panel-body">
          {approvals.isLoading ? (
            <div className="admin-inbox-list">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} style={{ height: 140, width: "100%" }} />
              ))}
            </div>
          ) : approvals.isError ? (
            <div className="admin-panel-body-padded">
              <p style={{ margin: 0, fontSize: 14, color: "var(--destructive)" }}>
                Failed to load approvals.{" "}
                {approvals.error instanceof Error ? approvals.error.message : "Unknown error"}
              </p>
            </div>
          ) : rows.length === 0 ? (
            <div className="admin-empty-state">
              <div className="admin-empty-state-icon">
                <Inbox style={{ width: 22, height: 22 }} />
              </div>
              <p className="admin-inbox-zero-title">Inbox zero</p>
              <p className="admin-empty-state-text">
                Nothing is waiting for a decision in {envLabel(environment)}.
              </p>
              <Button asChild variant="secondary" size="sm">
                <Link to="/requests">Browse requests</Link>
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="admin-empty-state">
              <p className="admin-empty-state-text">No items match your search</p>
              <Button variant="secondary" size="sm" onClick={() => setSearch("")}>
                Clear search
              </Button>
            </div>
          ) : (
            <div className="admin-inbox-list">
              {filtered.map((r) => (
                <InboxItem
                  key={r.id}
                  workspaceId={workspaceId}
                  environment={environment}
                  row={r}
                  canAct={canAct}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {canAct && stats.total > 0 ? (
        <section className="admin-panel" aria-labelledby="inbox-tips-heading">
          <div className="admin-panel-header">
            <h2 id="inbox-tips-heading" className="admin-panel-title">
              Review tips
            </h2>
          </div>
          <div className="admin-panel-body-padded">
            <ul
              style={{
                margin: 0,
                paddingLeft: 18,
                fontSize: 14,
                lineHeight: 1.55,
                color: "var(--muted-foreground)"
              }}
            >
              <li>Items are sorted by expiry — decide on urgent requests first.</li>
              <li>
                Open <strong style={{ color: "var(--foreground)" }}>View full request</strong> for
                payload, audit timeline, and permit details.
              </li>
              <li>Leave a comment when rejecting so operators understand the decision.</li>
            </ul>
          </div>
        </section>
      ) : null}
    </div>
  );
};
